import React from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Popconfirm,
    Typography,
    Tooltip,
} from 'antd';
import {
    UnlockOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;

interface ActiveLock {
    jobName: string;
    lockedBy: string;
    lockedAt: string;
    expiresAt: string;
    executionCount: number;
    lastSuccessfulAt?: string;
    lastError?: string;
    lastErrorAt?: string;
    isExpired?: boolean;
}

interface ActiveLocksTableProps {
    activeLocks: ActiveLock[];
    onForceUnlock: (jobName: string) => void;
    isUnlocking: boolean;
}

export const ActiveLocksTable: React.FC<ActiveLocksTableProps> = ({
    activeLocks,
    onForceUnlock,
    isUnlocking,
}) => {
    const columns = [
        {
            title: 'Job Name',
            dataIndex: 'jobName',
            key: 'jobName',
            render: (text: string) => (
                <Text strong style={{ fontFamily: 'monospace' }}>
                    {text}
                </Text>
            ),
        },
        {
            title: 'Locked By',
            dataIndex: 'lockedBy',
            key: 'lockedBy',
            render: (text: string) => (
                <Text code>{text}</Text>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: ActiveLock) => {
                if (record.isExpired) {
                    return (
                        <Tag color="red" icon={<ExclamationCircleOutlined />}>
                            Expired
                        </Tag>
                    );
                }
                return (
                    <Tag color="orange" icon={<ClockCircleOutlined />}>
                        Active
                    </Tag>
                );
            },
        },
        {
            title: 'Locked At',
            dataIndex: 'lockedAt',
            key: 'lockedAt',
            render: (date: string) => (
                <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm:ss')}>
                    <Text type="secondary">
                        {moment(date).fromNow()}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: 'Expires At',
            dataIndex: 'expiresAt',
            key: 'expiresAt',
            render: (date: string, record: ActiveLock) => (
                <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm:ss')}>
                    <Text type={record.isExpired ? 'danger' : 'secondary'}>
                        {moment(date).fromNow()}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: 'Executions',
            dataIndex: 'executionCount',
            key: 'executionCount',
            render: (count: number) => (
                <Text>{count.toLocaleString()}</Text>
            ),
        },
        {
            title: 'Last Success',
            dataIndex: 'lastSuccessfulAt',
            key: 'lastSuccessfulAt',
            render: (date?: string) => {
                if (!date) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm:ss')}>
                        <Text type="secondary">
                            {moment(date).fromNow()}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Last Error',
            key: 'lastError',
            render: (_: any, record: ActiveLock) => {
                if (!record.lastError || !record.lastErrorAt) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Tooltip title={`${record.lastError} (${moment(record.lastErrorAt).format('YYYY-MM-DD HH:mm:ss')})`}>
                        <Text type="danger" ellipsis style={{ maxWidth: 150 }}>
                            {record.lastError}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: ActiveLock) => (
                <Space>
                    <Popconfirm
                        title="Force Unlock Job"
                        description={`Are you sure you want to force unlock "${record.jobName}"? This may interrupt a running job.`}
                        onConfirm={() => onForceUnlock(record.jobName)}
                        okText="Force Unlock"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            danger
                            size="small"
                            icon={<UnlockOutlined />}
                            loading={isUnlocking}
                        >
                            Force Unlock
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (activeLocks.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">No active locks found</Text>
            </div>
        );
    }

    return (
        <Table
            columns={columns}
            dataSource={activeLocks}
            rowKey="jobName"
            pagination={false}
            scroll={{ x: 1200 }}
            size="small"
            rowClassName={(record) => record.isExpired ? 'row-expired' : ''}
            style={{
                '& .row-expired': {
                    backgroundColor: '#fff2f0',
                },
            } as any}
        />
    );
};